import { useEffect, useRef, useState } from "react";

// Touch-based pull-to-refresh for a scrollable element. Native/mobile only — on desktop
// there are no touch events so it stays inert. `onRefresh` is held in a ref so the listeners
// don't re-register on every parent render.
export function usePullToRefresh(scrollRef, onRefresh, enabled = true) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const cb = useRef(onRefresh);
  cb.current = onRefresh;
  const st = useRef({ startY: 0, pulling: false, pull: 0, refreshing: false });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;
    const THRESHOLD = 70;
    const set = (v) => { st.current.pull = v; setPull(v); };

    const onStart = (e) => {
      if (el.scrollTop <= 0 && !st.current.refreshing) {
        st.current.startY = e.touches[0].clientY;
        st.current.pulling = true;
      }
    };
    const onMove = (e) => {
      if (!st.current.pulling) return;
      const dy = e.touches[0].clientY - st.current.startY;
      if (dy > 0 && el.scrollTop <= 0) {
        set(Math.min(dy * 0.5, 90));
        if (dy > 8) e.preventDefault();
      } else {
        st.current.pulling = false;
        set(0);
      }
    };
    const onEnd = async () => {
      if (!st.current.pulling) return;
      st.current.pulling = false;
      if (st.current.pull >= THRESHOLD) {
        st.current.refreshing = true; setRefreshing(true); set(44);
        try { await cb.current?.(); } finally { st.current.refreshing = false; setRefreshing(false); set(0); }
      } else {
        set(0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [scrollRef, enabled]);

  return { pull, refreshing };
}
